import { RichIngredient } from "ts-packages/api-types/src";

import { useMemo } from "react";

import { Stack, Typography } from "@mui/material";

import { formatName } from "igfd-ui/src/utils/stringUtils";

import { IngredientListItem } from "./ingredient-list-item";

interface IngredientListProps {
  directlyLinkedIngredients?: RichIngredient[];
  ingredients?: RichIngredient[];
  filter?: string;
  selectedAllergenIds?: string[];
  optionalIngredientIds?: string[];
}

/**
 * Displays a list of ingredients with support for allergen filtering, search,
 * and marking optional ingredients. Ingredients are sorted with optional
 * first, then those containing allergens, then those without allergens.
 */
export const IngredientList = ({
  directlyLinkedIngredients,
  ingredients,
  filter,
  selectedAllergenIds,
  optionalIngredientIds,
}: IngredientListProps) => {
  // Filter ingredients linked to the dish
  const dishIngredients = useMemo(() => {
    const ingredientIds = directlyLinkedIngredients?.map(
      (a) => a.igfdTaxIngredientId
    );
    return ingredients?.filter((a) =>
      ingredientIds?.includes(a.igfdTaxIngredientId)
    );
  }, [directlyLinkedIngredients, ingredients]);

  // Ingredients with allergens matching any selected allergen IDs and matching filter text
  const ingredientsWithAllergens = useMemo(
    () =>
      dishIngredients?.filter((a) => {
        const hasAllergen = a.allergens.length > 0;
        const matchesAllergens = selectedAllergenIds?.length
          ? a.allergens.some((allergen) =>
              selectedAllergenIds.includes(allergen.igfdTaxAllergenId)
            )
          : true;

        return (
          hasAllergen &&
          matchesAllergens &&
          (!filter || a.name.toLowerCase().includes(filter.toLowerCase()))
        );
      }),
    [selectedAllergenIds, dishIngredients, filter]
  );

  // Ingredients without allergens matching filter text. `dishIngredients` has
  // to be a dependency: the drawer stays mounted while it steps from a dish
  // into one of its options, so without it this list keeps the previous dish's
  // ingredients and shows them under the new dish's name.
  const ingredientsWithOutAllergens = useMemo(
    () =>
      dishIngredients?.filter(
        (a) =>
          a.allergens.length === 0 &&
          (!filter || a.name.toLowerCase().includes(filter.toLowerCase()))
      ),
    [dishIngredients, filter]
  );

  // Combine and sort: optional first, then allergens, then non-allergen
  const allIngredients = useMemo(() => {
    const filtered = selectedAllergenIds?.length
      ? ingredientsWithAllergens
      : [
          ...(ingredientsWithAllergens || []),
          ...(ingredientsWithOutAllergens || []),
        ];

    return filtered?.sort((a, b) => {
      const isOptionalA =
        optionalIngredientIds?.includes(a.igfdTaxIngredientId) ?? false;
      const isOptionalB =
        optionalIngredientIds?.includes(b.igfdTaxIngredientId) ?? false;

      if (isOptionalA !== isOptionalB) return isOptionalA ? -1 : 1;

      const hasAllergensA = a.allergens.length > 0;
      const hasAllergensB = b.allergens.length > 0;

      if (hasAllergensA !== hasAllergensB) return hasAllergensA ? -1 : 1;

      return 0;
    });
  }, [
    selectedAllergenIds,
    ingredientsWithAllergens,
    ingredientsWithOutAllergens,
    optionalIngredientIds,
  ]);

  const ingredientCount = allIngredients?.length || 0;

  return (
    <Stack sx={{ width: "100%", mt: 4 }}>
      <Typography variant="labelRMedium" mb={1} color="text.secondary">
        {`${ingredientCount} ${
          ingredientCount === 1 ? "Ingredient" : "Ingredients"
        }`}
      </Typography>

      {allIngredients?.map((ingredient, index) => (
        <IngredientListItem
          key={ingredient.igfdTaxIngredientId}
          title={formatName(ingredient.name)}
          allergens={ingredient.allergens}
          showDivider={index < allIngredients.length - 1}
          isOptional={optionalIngredientIds?.includes(
            ingredient.igfdTaxIngredientId
          )}
        />
      ))}
    </Stack>
  );
};
